import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import { sync } from '@app/icare/client/sync'
import type { PatientFormIdType } from '@app/icare/db/schema/form/PatientFormCommon'
import { PatientFormId } from '@app/icare/db/schema/form/PatientFormCommon'
import { PatientFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import type { PatientIdType } from '@app/icare/entities/patient/schema/Patient'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import type { PatientConsentAndConfidentiality } from '@app/icare/entities/patientForm/schema/PatientForm'
import { PatientConsentAndConfidentialitySchema } from '@app/icare/entities/patientForm/schema/PatientForm'
import { appFormOptions } from '@app/icare/form/appFormOptions'
import { useAppForm } from '@app/icare/form/useAppForm'
import { t } from '@lingui/core/macro'
import { zodParams } from '@pkg/db/utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { Form } from 'antd'
import { z } from 'zod'
import { EditForm } from './-edit'
import { NewForm } from './-new'

export const Route = createFileRoute(
  '/_app/patienten/$id/_edit/_documents/_forms/_unterschriftdokumente/einwilligung-schweigepflicht/$form/',
)({
  component: RouteComponent,
  params: zodParams(z.object({ id: PatientId.ref, form: z.string() })),
  staticData: {
    layout: {
      menuRender: false,
    },
  },
  loader: async ({ params, context: { queryClient } }) => {
    if (params.form === 'new') return

    await queryClient.ensureQueryData(localQueryEntityOptions(params.id))
    await queryClient.ensureQueryData(localQueryEntityOptions(params.form as PatientFormIdType))
  },
})

function RouteComponent() {
  const { id, form: formId } = Route.useParams() as { id: PatientIdType; form: PatientFormIdType | 'new' }
  const router = useRouter()

  const isNew = formId === 'new'
  const current = useQuery(localQueryEntityOptions(formId))
  const editModeAllowed = current.data?.status === 'processing'

  const form = useAppForm(
    appFormOptions(PatientConsentAndConfidentialitySchema, {
      defaultValues: (current.data as PatientConsentAndConfidentiality) ?? {
        patientId: id,
        type: PatientFormTypeEnum.PatientConsentAndConfidentiality,
        status: 'new',
        id: PatientFormId.generate(),
      },
      toast: { successTitle: t`Einwilligung Schweigepflichtserklärung gespeichert` },
      onSubmit: async ({ value }) => {
        if (isNew) {
          await sync.mutate(value)
          await router.navigate({
            to: '/patienten/$id/unterschriftsdokumente',
            replace: true,
            params: { id },
          })
        } else {
          await sync.mutate(value)
        }
      },
    }),
  )

  return (
    <form.AppForm>
      <form.ProForm
        editablePolicy={isNew ? { forceMode: 'edit' } : !editModeAllowed ? { forceMode: 'read' } : undefined}
      >
        <Form component={false} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
          {isNew ? <NewForm /> : <EditForm />}
        </Form>
      </form.ProForm>
    </form.AppForm>
  )
}
